import axios, { AxiosError } from 'axios';
import {
  EligibilityAllEntities,
  EligibilityTab,
  PlanNameOption,
  WithJoinFields,
} from '../components/eligibility/eligibility.types';
import { TaskView } from '../components/eligibility/tasks/tasks.types';
import {
  Coupon,
  CouponStatus,
  CreateCouponPayload,
  CreateOrUpdateEntityResponse,
  EditableInsurancePlanFields,
  EditableLeadFields,
  EditablePayerFields,
  EligibilityStatus,
  EligibilityTaskLookup,
  GetListedResourcePayload,
  GetPayerListResponse,
  InsurancePlanPaginated,
  InsurancePlansPaginatedResponse,
  MembershipComputeByVersionPayload,
  MembershipComputeByVersionResponse,
  PVerifyEligibility,
  PaginatedEntities,
  PaginatedEntitiesResult,
  PaginationParams,
  Payer,
  PayerFilteredResponse,
  PayerPaginatedResponse,
  ProcessACEResponse,
  UpdateCouponPayload,
} from '../interfaces/eligibility';
import { User } from '../interfaces/provider';
import { Roles } from '../util/constants';
import { AuthService } from './';
import { CommonService } from './common';

const ELIGIBILITY_BASE_URL = process.env.REACT_APP_PUBLIC_ELIGIBILITY_BASE_URL;

/**
 * The task list's sort for the crmId lookup. `crm_id` is not unique in eligibility-api (manual
 * task creation dedupes on insurance id and date of birth, not on the CRM lead), and the list's
 * default sort is `requiredDate DESC`, which can put a completed task ahead of the current one.
 * The list sorts by one column only, so the lookup takes the most recently created task: `id`
 * is the lead table's serial key.
 */
const TASK_BY_CRM_ID_SORT = { sortBy: 'id', order: 'DESC' } as const;
const DEFAULT_PER_PAGE = 30;
const DEFAULT_PAYER_LIST_PAGE_SIZE = 300;

class EligibilityService {
  /**
   * One page of an eligibility list endpoint (`/v1/<resource>`). `filters` travels as a JSON
   * string in its own query parameter, which is not on the URL-privacy allow-list, so its value
   * is redacted from Sentry. Throws on failure; callers decide how to report it.
   */
  private async fetchPage<T>({
    perPage,
    page,
    resource,
    sortBy,
    order,
    filters,
  }: Pick<PaginationParams, 'perPage' | 'page' | 'resource' | 'sortBy' | 'order' | 'filters'>) {
    const auth = AuthService.getAuth();

    return (
      await axios.get<{ entities: T[]; totalCount: number }>(
        `${ELIGIBILITY_BASE_URL}/v1/${resource}`,
        {
          headers: { Authorization: `Bearer ${auth?.auth_token}` },
          params: {
            perPage,
            page,
            sortBy,
            order,
            filters: JSON.stringify(filters),
          },
        },
      )
    ).data;
  }

  public async listPaginatedData<T extends Payer | InsurancePlanPaginated | TaskView>({
    perPage,
    page,
    resource,
    sortBy,
    order,
    filters,
  }: PaginationParams): Promise<PaginatedEntitiesResult<T> | null> {
    try {
      const { entities, totalCount } = await this.fetchPage<T>({
        perPage,
        page,
        resource,
        sortBy,
        order,
        filters,
      });

      return {
        entities: entities.reduce(
          (allEntities, entity, index) => ({
            ...allEntities,
            [entity.id]: { ...entity, rowId: index + 1 },
          }),
          {} as PaginatedEntities<T>,
        ),
        totalCount,
      };
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  public async listInsurancesByQueryParams({
    payerId,
    clinicId,
  }: {
    payerId: string;
    clinicId: string;
  }): Promise<PlanNameOption[] | null> {
    const auth = AuthService.getAuth();

    try {
      const planNameOptions = (
        await axios.get<{ success: boolean; insurances: PlanNameOption[] }>(
          `${ELIGIBILITY_BASE_URL}/v1/entities/insurance-plans`,
          {
            headers: { Authorization: `Bearer ${auth?.auth_token}` },
            params: { payerId, clinicId },
          },
        )
      ).data;

      return planNameOptions.insurances;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  public async getDetails<T>({
    id,
    resource,
  }: {
    id: string;
    resource: EligibilityTab;
  }): Promise<T | null> {
    const auth = AuthService.getAuth();

    try {
      const response = (
        await axios.get<T>(`${ELIGIBILITY_BASE_URL}/v1/${resource}/${id}`, {
          headers: { Authorization: `Bearer ${auth?.auth_token}` },
        })
      ).data;

      return response || null;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  public async editDetails<T>({
    id,
    fields,
    deleted,
    resource,
  }: {
    id: string;
    deleted?: boolean;
    fields:
      | EditablePayerFields
      | EditableLeadFields
      | Omit<EditableInsurancePlanFields, 'planCategory'>;
    resource: EligibilityTab;
  }): Promise<
    | CreateOrUpdateEntityResponse<T>
    | {
        success: boolean;
        message?: string;
      }
    | null
  > {
    const auth = AuthService.getAuth();

    try {
      return (
        await axios.put<CreateOrUpdateEntityResponse<T>>(
          `${ELIGIBILITY_BASE_URL}/v1/${resource}/${id}`,
          { ...fields, updatedBy: auth?.user_id, ...(deleted && { deleted }) },
          { headers: { Authorization: `Bearer ${auth?.auth_token}` } },
        )
      ).data;
    } catch (error) {
      console.error(error);

      const axiosError = error as AxiosError;
      const reason = axiosError.response?.data?.reason || axiosError.response?.data?.error;
      if (reason) {
        return {
          success: false,
          message: reason,
        };
      }
    }

    return null;
  }

  public async deleteLead(id: string): Promise<{
    success: boolean;
    message?: string;
    data?: TaskView;
  }> {
    const auth = AuthService.getAuth();

    try {
      const response = (
        await axios.put<TaskView>(
          `${ELIGIBILITY_BASE_URL}/v1/leads/${id}`,
          { deleted: true, updatedBy: auth?.user_id },
          { headers: { Authorization: `Bearer ${auth?.auth_token}` } },
        )
      ).data;

      return {
        success: true,
        message: 'Task was successfully deleted',
        data: response,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        message: 'Failed to delete task',
      };
    }
  }

  public async createRegister<T>({
    fields,
    resource,
  }: {
    fields: EditablePayerFields | EditableLeadFields | EditableInsurancePlanFields;
    resource: EligibilityTab;
  }): Promise<
    | CreateOrUpdateEntityResponse<T>
    | {
        success: boolean;
        message?: string;
      }
    | null
  > {
    const auth = AuthService.getAuth();

    try {
      const response = (
        await axios.post<CreateOrUpdateEntityResponse<T>>(
          `${ELIGIBILITY_BASE_URL}/v1/${resource}`,
          { ...fields, createdBy: auth?.user_id },
          { headers: { Authorization: `Bearer ${auth?.auth_token}` } },
        )
      ).data;

      return response;
    } catch (error) {
      console.error(error);

      const axiosError = error as AxiosError;
      const reason = axiosError.response?.data?.reason || axiosError.response?.data?.error;
      if (reason) {
        return {
          success: false,
          message: reason,
        };
      }
    }

    return null;
  }

  public async listData<T>({
    entity,
    filters,
  }: {
    entity: EligibilityAllEntities;
    filters?: WithJoinFields;
  }): Promise<{
    success: boolean;
    totalCount: number;
    entities: T[];
  } | null> {
    const auth = AuthService.getAuth();

    try {
      const response = (
        await axios.get<{
          success: boolean;
          totalCount: number;
          entities: T[];
        }>(`${ELIGIBILITY_BASE_URL}/v1/entities?entryType=${entity}`, {
          headers: { Authorization: `Bearer ${auth?.auth_token}` },
          params: {
            ...(filters && { filters: filters }),
          },
        })
      ).data;

      return response || null;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  public async getEntityInfoByQueryParams<T>({
    property,
    value,
    entity,
  }: {
    property: string;
    value: string;
    entity: EligibilityAllEntities;
  }): Promise<T | null> {
    const auth = AuthService.getAuth();

    try {
      return (
        await axios.get<T>(
          `${ELIGIBILITY_BASE_URL}/v1.1/entities?entryType=${entity}&property=${property}&value=${value}`,
          { headers: { Authorization: `Bearer ${auth?.auth_token}` } },
        )
      ).data;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  /**
   * The eligibility task that belongs to a CRM lead (LeadSquared id).
   *
   * The task list is scoped to the user's permitted clinics, and its exact `crmId` filter comes
   * from eligibility-api #389. A lead usually has one task, but `crm_id` is not unique there, so
   * the list is sorted newest first and the first match is the one linked. An empty page means
   * "no task yet".
   *
   * An eligibility-api without that filter ignores the unknown key and returns unfiltered tasks.
   * A page of tasks none of which has the `crmId` asked for is that case, so it is
   * `unavailable`, not "no task yet": the lookup cannot answer, and saying the lead has no task
   * would be wrong. There is deliberately no fallback to the unscoped `/v1.1/entities` lookup.
   *
   * Only the task id is returned: the response carries whole leads, and the caller needs nothing
   * but where to link. The CRM id travels only inside the `filters` request parameter, and
   * failures are logged by status only, never with the URL.
   */
  public async findTaskByCrmId(crmId: string): Promise<EligibilityTaskLookup> {
    try {
      const { entities } = await this.fetchPage<{ id: number; crmId?: string | null }>({
        page: 1,
        // The newest task is the one linked; against an API that ignores the filter, one row
        // also keeps the unrelated tasks fetched to one.
        perPage: 1,
        resource: EligibilityTab.Leads,
        filters: { crmId },
        ...TASK_BY_CRM_ID_SORT,
      });
      const page = entities ?? [];
      const task = page.find((entity) => entity.crmId === crmId);

      if (task?.id) {
        return { status: 'found', taskId: task.id };
      }

      if (page.length > 0) {
        console.error('Eligibility task lookup unavailable: the crmId filter was ignored');
        return { status: 'unavailable' };
      }

      return { status: 'none' };
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;

      if (status === 401 || status === 403) {
        return { status: 'forbidden' };
      }

      console.error(`Eligibility task lookup failed${status ? ` (${status})` : ''}`);
      return { status: 'error' };
    }
  }

  async getTaskOwners(): Promise<User[] | null> {
    try {
      const roles = await CommonService.getRoles();
      const roleFound = roles.find((role) => {
        return role.name === Roles.Billing;
      });

      if (!roleFound) {
        return null;
      }

      const resp = await CommonService.getUserByRole(roleFound.id);
      if (!resp?.success) {
        console.warn("Can't get task owners...");
        return null;
      }

      return resp.data;
    } catch (err) {
      console.error('getTaskOwners::err: ', err);
    }

    return null;
  }

  async getPVerifyInsuranceStatus({
    eligibilityData,
  }: {
    eligibilityData: PVerifyEligibility;
  }): Promise<EligibilityStatus | null> {
    const auth = AuthService.getAuth();
    const url = `${ELIGIBILITY_BASE_URL}/v1/insurances/status`;

    try {
      const response = await axios.post<EligibilityStatus>(url, eligibilityData, {
        headers: { Authorization: `Bearer ${auth?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async computeMembership(
    payload: MembershipComputeByVersionPayload,
  ): Promise<MembershipComputeByVersionResponse | null> {
    const auth = AuthService.getAuth();
    const url = `${ELIGIBILITY_BASE_URL}/v1/membership/compute`;

    try {
      const response = await axios.post<MembershipComputeByVersionResponse>(
        url,
        { ...payload },
        { headers: { Authorization: `Bearer ${auth?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      console.error(error);
    }
    return null;
  }

  public async getPayerPaginated({
    perPage = DEFAULT_PER_PAGE,
    page,
    clinicId,
    filters,
  }: {
    clinicId?: string;
    page: number;
    perPage?: number;
    filters?: { name?: string; payerId?: string };
  }): Promise<Payer[]> {
    const auth = AuthService.getAuth();

    const response = (
      await axios.get<PayerPaginatedResponse>(`${ELIGIBILITY_BASE_URL}/v1/payers`, {
        params: {
          page,
          ...(perPage && { pageSize: perPage }),
          ...(clinicId && { clinicId }),
          ...(filters && { filters: JSON.stringify(filters) }),
        },
        headers: { Authorization: `Bearer ${auth?.auth_token}` },
      })
    ).data;

    if (!response?.success) {
      return [];
    }

    return response.entities;
  }

  public async getInsurancePlansPaginated({
    perPage = DEFAULT_PER_PAGE,
    page,
    filters,
  }: {
    page: number;
    perPage?: number;
    filters?: {
      payerId?: string;
      clinicId?: string;
      groupId?: string;
      payerName?: string;
    };
  }): Promise<InsurancePlansPaginatedResponse> {
    const auth = AuthService.getAuth();

    const config = {
      params: {
        page,
        ...(perPage && { pageSize: perPage }),
        ...(filters && { filters: JSON.stringify(filters) }),
      },
      headers: { Authorization: `Bearer ${auth?.auth_token}` },
    };

    const response = (
      await axios.get<InsurancePlansPaginatedResponse>(
        `${ELIGIBILITY_BASE_URL}/v1/insurance-plans`,
        config,
      )
    ).data;

    return response;
  }

  public async processACECalendars({
    cookie,
    content,
    clinicId,
  }: {
    cookie: string;
    content: string;
    clinicId: string;
  }): Promise<ProcessACEResponse | null> {
    const auth = AuthService.getAuth();
    const url = `${ELIGIBILITY_BASE_URL}/v1/ace/calendars`;

    try {
      const response = await axios.post<ProcessACEResponse>(
        url,
        { cookie, calendarRaw: content, clinicId },
        { headers: { Authorization: `Bearer ${auth?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getPayers({ filter }: { filter?: string | null }) {
    const auth = AuthService.getAuth();
    const url = `${ELIGIBILITY_BASE_URL}/v2/payers`;

    try {
      const response = await axios.get<PayerFilteredResponse>(url, {
        params: {
          ...(filter && { filter }),
        },
        headers: { Authorization: `Bearer ${auth?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  public async getCoupons(params: {
    page?: number;
    perPage?: number;
    filters?: {
      code?: string;
      status?: CouponStatus;
      clinicId?: number;
      validFrom?: string | null;
      validTo?: string | null;
    };
  }): Promise<{
    coupons: Coupon[];
    pagination: { totalCount: number; currentPage: number; perPage: number };
  }> {
    const auth = AuthService.getAuth();
    const url = `${ELIGIBILITY_BASE_URL}/v1/coupons`;

    try {
      const response = await axios.get<{
        coupons: Coupon[];
        pagination: {
          totalCount: number;
          currentPage: number;
          perPage: number;
        };
      }>(url, {
        headers: { Authorization: `Bearer ${auth?.auth_token}` },
        params: {
          ...(params.page && { page: params.page || 1 }),
          ...(params.perPage && { perPage: params.perPage || DEFAULT_PER_PAGE }),
          ...(params.filters?.code && { code: params.filters.code }),
          ...(params.filters?.status && { status: params.filters.status }),
          ...(params.filters?.clinicId && { clinicIds: params.filters.clinicId }),
          ...(params.filters?.validFrom && { validFrom: params.filters.validFrom }),
          ...(params.filters?.validTo && { validTo: params.filters.validTo }),
        },
      });

      return response.data;
    } catch (error) {
      console.error(error);
      return {
        coupons: [],
        pagination: {
          totalCount: 0,
          currentPage: 0,
          perPage: 0,
        },
      };
    }
  }

  public async createCoupon(coupon: CreateCouponPayload) {
    const auth = AuthService.getAuth();
    const url = `${ELIGIBILITY_BASE_URL}/v1/coupons`;

    try {
      const response = await axios.post<Coupon>(url, coupon, {
        headers: { Authorization: `Bearer ${auth?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  public async updateCoupon(couponId: number, coupon: UpdateCouponPayload) {
    const auth = AuthService.getAuth();
    const url = `${ELIGIBILITY_BASE_URL}/v1/coupons/${couponId}`;

    try {
      const response = await axios.put<Coupon>(url, coupon, {
        headers: { Authorization: `Bearer ${auth?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  public async getPayerList({
    clinicId,
    filter,
  }: GetListedResourcePayload & { clinicId: number }): Promise<Payer[]> {
    let page = 1;
    let currentPagePayersCount = 0;
    let payers: Payer[] = [];

    do {
      const response = await axios.get<GetPayerListResponse>(`${ELIGIBILITY_BASE_URL}/v2/payers`, {
        params: {
          page,
          pageSize: DEFAULT_PAYER_LIST_PAGE_SIZE,
          clinicId,
          ...(filter && { filter }),
        },
      });

      if (!response.data.success) {
        currentPagePayersCount = 0;
      } else {
        payers = response?.data?.data ?? [];
        currentPagePayersCount = response.data.data.length;
        page++;
      }
    } while (currentPagePayersCount >= DEFAULT_PAYER_LIST_PAGE_SIZE);

    return payers;
  }
}

const eligibilityService = new EligibilityService();

export default eligibilityService;
